/**
 * data-quality.ts — Step 1.5: Averaging, outlier detection, smoothing, gap filling.
 *
 * cleanMeasurements(patches, options) → CleaningResult
 *
 * Sub-steps (all exported for unit testing):
 *   A  averageRepeats    — merge same SAMPLE_ID measurements
 *   B  detectOutliers    — flag curve residual + IQR outliers
 *   C  smoothGroups      — Gaussian 1-D smoothing per group
 *   D  fillGaps          — interpolate outlier slots; extrapolate short ramps
 *   E  validateCoverage  — coverage report + proceed/caution/reprint
 */

import type {
  CleaningOptions,
  CleaningReport,
  CleaningResult,
  GroupCleaningReport,
  Lab,
  MeasuredPatch,
  PatchGroupId,
} from '@/types';
import { DEFAULT_PATCH_FLAGS } from '@/types';
import { CHANNEL_IDX, deltaE76, mean, percentile, sortByInk, stdDev } from '@/engine/colormath';

// =============================================================================
// PostCleanValidation (local interface — not in shared types)
// =============================================================================

export interface GroupCoverage {
  stepCount:        number;
  measured:         number;
  interpolated:     number;
  extrapolated:     number;
  coveragePercent:  number;
}

export interface PostCleanValidation {
  ok:                  boolean;
  warnings:            string[];
  groupCoverage:       Partial<Record<PatchGroupId, GroupCoverage>>;
  minMeasuredCoverage: number;
  recommendation:      'proceed' | 'caution' | 'reprint';
}

// =============================================================================
// Internal helpers
// =============================================================================

const ALL_GROUPS: PatchGroupId[] = [
  'R', 'G', 'B', 'RG', 'RB', 'GB', 'N',
  'NN32', 'NN64', 'NN96', 'NN128', 'NN160', 'NN192', 'NN224',
];

function groupBy(patches: MeasuredPatch[]): Map<PatchGroupId, MeasuredPatch[]> {
  const m = new Map<PatchGroupId, MeasuredPatch[]>();
  for (const p of patches) {
    const arr = m.get(p.group) ?? [];
    arr.push(p);
    m.set(p.group, arr);
  }
  return m;
}

function computeMeanLab(labs: Lab[]): Lab {
  const n = labs.length;
  return [
    labs.reduce((s, l) => s + l[0], 0) / n,
    labs.reduce((s, l) => s + l[1], 0) / n,
    labs.reduce((s, l) => s + l[2], 0) / n,
  ];
}

// =============================================================================
// A — Average repeated measurements
// =============================================================================

export function averageRepeats(
  patches: MeasuredPatch[],
  noiseThreshold = 0.8,
): MeasuredPatch[] {
  // Group patches by SAMPLE_ID
  const byId = new Map<string, MeasuredPatch[]>();
  for (const p of patches) {
    const arr = byId.get(p.id) ?? [];
    arr.push(p);
    byId.set(p.id, arr);
  }

  const result: MeasuredPatch[] = [];

  for (const [, group] of byId) {
    const base = group[0]!;

    if (group.length === 1) {
      result.push({ ...base, flags: { ...base.flags, repeatsCount: 1, repeatStdDev: 0 } });
      continue;
    }

    // First pass: compute mean + std dev, exclude Chauvenet sub-outliers (> 3σ)
    const mean1 = computeMeanLab(group.map(p => p.lab));
    const devs  = group.map(p => deltaE76(p.lab, mean1));
    const sigma = stdDev(devs);

    const valid = sigma < 1e-10
      ? group
      : group.filter((_, i) => devs[i]! <= 3 * sigma);
    const keepGroup = valid.length > 0 ? valid : group;

    const finalMean    = computeMeanLab(keepGroup.map(p => p.lab));
    const finalStdDev  = keepGroup.length > 1
      ? mean(keepGroup.map(p => deltaE76(p.lab, finalMean)))
      : 0;

    result.push({
      ...base,
      lab: finalMean,
      flags: {
        ...base.flags,
        repeatsCount: group.length,
        repeatStdDev: finalStdDev,
        noisy: finalStdDev > noiseThreshold,
      },
    });
  }

  return result;
}

// =============================================================================
// B — Outlier detection
// =============================================================================

/** Residual method: flag patches whose Lab is > threshold ΔE from linear interpolation
 *  of neighbours. Endpoints (first/last in group) are never flagged. */
function residualOutliers(group: MeasuredPatch[], threshold: number): Set<string> {
  const sorted = sortByInk(group);
  const ids    = new Set<string>();

  for (let i = 1; i < sorted.length - 1; i++) {
    const prev   = sorted[i - 1]!.lab;
    const next   = sorted[i + 1]!.lab;
    // Midpoint reference (t=0.5)
    const refLab: Lab = [
      (prev[0] + next[0]) / 2,
      (prev[1] + next[1]) / 2,
      (prev[2] + next[2]) / 2,
    ];
    if (deltaE76(sorted[i]!.lab, refLab) > threshold) ids.add(sorted[i]!.id);
  }
  return ids;
}

/** IQR method: flag patches whose ΔL* step is outside [Q1 - k·IQR, Q3 + k·IQR]. */
function iqrOutliers(group: MeasuredPatch[], iqrMultiplier: number): Set<string> {
  const sorted = sortByInk(group);
  if (sorted.length < 3) return new Set();

  const deltas = sorted.slice(1).map((p, i) => p.lab[0] - sorted[i]!.lab[0]);
  const q1  = percentile(deltas, 25);
  const q3  = percentile(deltas, 75);
  const iqr = q3 - q1;
  const lo  = q1 - iqr * iqrMultiplier;
  const hi  = q3 + iqr * iqrMultiplier;

  const ids = new Set<string>();
  deltas.forEach((d, i) => {
    if (d < lo || d > hi) ids.add(sorted[i + 1]!.id);
  });
  return ids;
}

export function detectOutliers(
  patches:       MeasuredPatch[],
  method:        'residual' | 'iqr' | 'both' = 'both',
  thresholdDE  = 2.5,
  iqrMultiplier = 2.0,
): MeasuredPatch[] {
  const grouped = groupBy(patches);
  const outlierIds = new Set<string>();

  for (const [, group] of grouped) {
    if (group.length < 3) continue;

    if (method === 'residual' || method === 'both') {
      for (const id of residualOutliers(group, thresholdDE)) outlierIds.add(id);
    }
    if (method === 'iqr' || method === 'both') {
      for (const id of iqrOutliers(group, iqrMultiplier)) outlierIds.add(id);
    }
  }

  // Safety: never flag a patch if it's the only one left in its group
  const groupCount = new Map<PatchGroupId, number>();
  for (const p of patches) groupCount.set(p.group, (groupCount.get(p.group) ?? 0) + 1);

  return patches.map(p => {
    if (!outlierIds.has(p.id)) return p;
    // Demote to noisy if it's the sole patch in the group
    if ((groupCount.get(p.group) ?? 0) <= 1) {
      return { ...p, flags: { ...p.flags, noisy: true } };
    }
    return { ...p, flags: { ...p.flags, outlier: true } };
  });
}

// =============================================================================
// C — Gaussian smoothing
// =============================================================================

export function gaussianSmooth1D(values: number[], sigma: number): number[] {
  if (sigma <= 0 || values.length < 2) return [...values];

  const r      = Math.ceil(2.5 * sigma);
  const kernel = Array.from({ length: 2 * r + 1 }, (_, i) => {
    const z = (i - r) / sigma;
    return Math.exp(-0.5 * z * z);
  });
  const kSum      = kernel.reduce((s, v) => s + v, 0);
  const normalized = kernel.map(v => v / kSum);

  // Reflecting boundary padding
  const padded = [
    ...values.slice(1, r + 1).reverse(),
    ...values,
    ...values.slice(-r - 1, -1).reverse(),
  ];

  return values.map((_, i) =>
    normalized.reduce((s, w, ki) => s + w * (padded[i + ki] ?? 0), 0),
  );
}

export function smoothGroup(patches: MeasuredPatch[], sigma: number): MeasuredPatch[] {
  const valid  = sortByInk(patches.filter(p => !p.flags.outlier));
  if (valid.length < 3 || sigma <= 0) return patches;

  let currentSigma = sigma;
  let Ls: number[] = valid.map(p => p.lab[0]);
  let as_: number[] = valid.map(p => p.lab[1]);
  let bs_: number[] = valid.map(p => p.lab[2]);

  // Try smoothing, backing off sigma if monotonicity is violated
  for (let attempt = 0; attempt < 6; attempt++) {
    if (currentSigma <= 0) break;
    const smoothL = gaussianSmooth1D(Ls, currentSigma);

    // Preserve endpoints
    smoothL[0]               = Ls[0]!;
    smoothL[smoothL.length - 1] = Ls[Ls.length - 1]!;

    // Check monotonicity (L* should be non-increasing as ink increases)
    let mono = true;
    for (let i = 1; i < smoothL.length; i++) {
      if ((smoothL[i] ?? 0) > (smoothL[i - 1] ?? 0) + 1e-4) { mono = false; break; }
    }

    if (mono) {
      Ls  = smoothL;
      as_ = gaussianSmooth1D(as_, currentSigma);
      bs_ = gaussianSmooth1D(bs_, currentSigma);
      // Restore endpoints for a* and b*
      as_[0] = valid[0]!.lab[1];    as_[as_.length - 1] = valid[valid.length - 1]!.lab[1];
      bs_[0] = valid[0]!.lab[2];    bs_[bs_.length - 1] = valid[valid.length - 1]!.lab[2];
      break;
    }
    currentSigma -= 0.2;
  }

  // Build a map from patch id → new lab
  const smoothed = new Map<string, Lab>();
  valid.forEach((p, i) => smoothed.set(p.id, [Ls[i]!, as_[i]!, bs_[i]!]));

  return patches.map(p => {
    const newLab = smoothed.get(p.id);
    return newLab ? { ...p, lab: newLab } : p;
  });
}

// =============================================================================
// D — Monotone cubic spline (Fritsch–Carlson)
// =============================================================================

function buildMonotoneCubicSpline(
  xs: number[],
  ys: number[],
): (x: number) => number {
  const n = xs.length;
  if (n < 2) return () => ys[0] ?? 0;
  if (n === 2) {
    return (x: number) => {
      const t = (x - xs[0]!) / ((xs[1]! - xs[0]!) || 1);
      return ys[0]! + (ys[1]! - ys[0]!) * t;
    };
  }

  const h     = xs.slice(1).map((x, i) => x - xs[i]!);
  const delta = ys.slice(1).map((y, i) => (y - ys[i]!) / (h[i]! || 1));

  // Compute tangents
  const m = new Array<number>(n).fill(0);
  m[0]     = delta[0]!;
  m[n - 1] = delta[n - 2]!;
  for (let i = 1; i < n - 1; i++) {
    if ((delta[i - 1]! * delta[i]!) <= 0) { m[i] = 0; continue; }
    const w1 = 2 * h[i]! + h[i - 1]!;
    const w2 = h[i]! + 2 * h[i - 1]!;
    m[i] = (w1 + w2) / (w1 / delta[i - 1]! + w2 / delta[i]!);
  }

  // Enforce monotonicity (alpha/beta conditions)
  for (let i = 0; i < n - 1; i++) {
    if (Math.abs(delta[i]!) < 1e-10) { m[i] = 0; m[i + 1] = 0; continue; }
    const alpha = m[i]! / delta[i]!;
    const beta  = m[i + 1]! / delta[i]!;
    const r     = Math.sqrt(alpha * alpha + beta * beta);
    if (r > 3) { m[i]! && (m[i] = m[i]! * 3 / r); m[i + 1]! && (m[i + 1] = m[i + 1]! * 3 / r); }
  }

  return (x: number): number => {
    // Binary search for segment
    let lo = 0, hi = n - 2;
    while (lo < hi) {
      const mid = (lo + hi) >> 1;
      if (xs[mid + 1]! < x) lo = mid + 1; else hi = mid;
    }
    const i  = lo;
    const hi_ = h[i]! || 1;
    const t  = (x - xs[i]!) / hi_;
    const t2 = t * t;
    const t3 = t2 * t;
    return (2 * t3 - 3 * t2 + 1) * ys[i]!
         + (t3 - 2 * t2 + t) * hi_ * m[i]!
         + (-2 * t3 + 3 * t2) * ys[i + 1]!
         + (t3 - t2) * hi_ * m[i + 1]!;
  };
}

// =============================================================================
// D — Gap filling (interpolation + extrapolation)
// =============================================================================

export function fillGaps(
  patches: MeasuredPatch[],
  opts: { interpolateGaps: boolean; extrapolateEdges: boolean; maxExtrapolationSteps: number },
): MeasuredPatch[] {
  const grouped = groupBy(patches);
  const result: MeasuredPatch[] = [];

  for (const [group, groupPatches] of grouped) {
    const ch    = CHANNEL_IDX[group] ?? 0;
    const valid = sortByInk(groupPatches.filter(p => !p.flags.outlier));
    const all   = sortByInk(groupPatches);

    if (valid.length === 0) {
      // Entire group is outliers — keep as-is, can't interpolate
      result.push(...groupPatches);
      continue;
    }

    // ── D1: Interpolate outlier-flagged patches ─────────────────────────────
    if (opts.interpolateGaps && valid.length >= 2) {
      const xs = valid.map(p => p.rgb[ch]);
      // Build spline per Lab channel (descending x = high ink side)
      // x values must be monotone for spline; sort ascending for spline
      // noUncheckedIndexedAccess: protect all array indexing used in comparisons/mapping
      const xsAsc = [...xs].map(v => v ?? 0).sort((a, b) => a - b);
      const validSortedAsc = [...valid].sort((a, b) => (a.rgb[ch] ?? 0) - (b.rgb[ch] ?? 0));

      const splineL = buildMonotoneCubicSpline(xsAsc, validSortedAsc.map(p => p.lab[0] ?? 0));
      const splineA = buildMonotoneCubicSpline(xsAsc, validSortedAsc.map(p => p.lab[1] ?? 0));
      const splineB = buildMonotoneCubicSpline(xsAsc, validSortedAsc.map(p => p.lab[2] ?? 0));

      const xMin = xsAsc[0] ?? 0;
      const xMax = xsAsc[xsAsc.length - 1] ?? 0;

      for (const p of all) {
        if (!p.flags.outlier) {
          result.push(p);
        } else {
          const x = p.rgb[ch] ?? 0;
          if (x >= xMin && x <= xMax) {
            // Within range: interpolate
            const newLab: Lab = [
              Math.max(0, Math.min(100, splineL(x))),
              splineA(x),
              splineB(x),
            ];
            result.push({
              ...p,
              lab: newLab,
              flags: { ...p.flags, outlier: false, interpolated: true },
            });
          } else {
            // Outside range: keep outlier (extrapolation handles this separately)
            result.push(p);
          }
        }
      }
    } else {
      result.push(...groupPatches);
    }

    // ── D2: Extrapolate beyond measured range (ramp groups only) ─────────────
    const RAMP_GROUPS = new Set<PatchGroupId>(['R', 'G', 'B', 'RG', 'RB', 'GB', 'N']);
    if (opts.extrapolateEdges && opts.maxExtrapolationSteps > 0 && valid.length >= 2 && RAMP_GROUPS.has(group)) {
      // Detect step size from valid patches (median of consecutive differences)
      const validAsc = [...valid]
        .map(p => ({ p, v: (p.rgb[ch] ?? 0) as number }))
        .sort((a, b) => a.v - b.v)
        .map(x => x.p);

      const diffs = validAsc.slice(1).map((p, i) => (p.rgb[ch] ?? 0) - (validAsc[i]!.rgb[ch] ?? 0));
      const sortedDiffs = [...diffs].filter(d => d > 0).sort((a, b) => a - b);
      if (sortedDiffs.length === 0) continue;
      const stepSize = sortedDiffs[Math.floor(sortedDiffs.length / 2)] ?? 0;

      // Extrapolate at the low-channel-value end (high ink = beyond last valid)
      const minChVal = validAsc[0]?.rgb[ch] ?? 0;
      const p1 = validAsc[1];
      const p0 = validAsc[0];
      if (!p0 || !p1) continue;

      for (let k = 1; k <= opts.maxExtrapolationSteps; k++) {
        const targetCh = minChVal - k * stepSize;
        if (targetCh < 0) break;

        const p0Ch = p0.rgb[ch] ?? 0;
        const p1Ch = p1.rgb[ch] ?? 0;
        const dx = (p0Ch - p1Ch) || 1;
        const t  = (targetCh - p0Ch) / dx;

        const extraLab: Lab = [
          Math.max(0, Math.min(100, (p0.lab[0] ?? 0) + ((p0.lab[0] ?? 0) - (p1.lab[0] ?? 0)) * t)),
          Math.max(-128, Math.min(127, (p0.lab[1] ?? 0) + ((p0.lab[1] ?? 0) - (p1.lab[1] ?? 0)) * t)),
          Math.max(-128, Math.min(127, (p0.lab[2] ?? 0) + ((p0.lab[2] ?? 0) - (p1.lab[2] ?? 0)) * t)),
        ];

        const extRgb: [number, number, number] = [...p0.rgb] as [number, number, number];
        extRgb[ch] = targetCh;

        result.push({
          id:    `${p0.id}_ext${k}`,
          group,
          rgb:   extRgb,
          cmy:   [1 - extRgb[0] / 255, 1 - extRgb[1] / 255, 1 - extRgb[2] / 255] as [number, number, number],
          lab:   extraLab,
          flags: {
            ...DEFAULT_PATCH_FLAGS,
            extrapolated: true,
            repeatsCount: 0,
            repeatStdDev: 0,
          },
        });
      }
    }
  }

  return result;
}

// =============================================================================
// E — Post-clean coverage validation
// =============================================================================

export function validateCoverage(patches: MeasuredPatch[]): PostCleanValidation {
  const grouped  = groupBy(patches);
  const coverage: Partial<Record<PatchGroupId, GroupCoverage>> = {};
  const warnings: string[] = [];
  let   minCov   = 100;

  for (const group of ALL_GROUPS) {
    const grp = grouped.get(group);
    if (!grp || grp.length === 0) continue;

    const stepCount    = grp.length;
    const measured     = grp.filter(p => !p.flags.interpolated && !p.flags.extrapolated && !p.flags.outlier).length;
    const interpolated = grp.filter(p => p.flags.interpolated).length;
    const extrapolated = grp.filter(p => p.flags.extrapolated).length;
    const covPct       = stepCount > 0 ? (measured / stepCount) * 100 : 0;

    coverage[group] = { stepCount, measured, interpolated, extrapolated, coveragePercent: covPct };
    if (covPct < minCov) minCov = covPct;
    if (covPct < 70) warnings.push(`Group ${group}: only ${covPct.toFixed(0)}% measured`);
  }

  const recommendation: 'proceed' | 'caution' | 'reprint' =
    minCov >= 70  ? 'proceed'  :
    minCov >= 40  ? 'caution'  : 'reprint';

  return {
    ok:                  minCov >= 70,
    warnings,
    groupCoverage:       coverage,
    minMeasuredCoverage: minCov,
    recommendation,
  };
}

// =============================================================================
// Main: cleanMeasurements
// =============================================================================

export function cleanMeasurements(
  patches:  MeasuredPatch[],
  options:  CleaningOptions,
): CleaningResult {
  // ── A: Average repeats ─────────────────────────────────────────────────────
  let working = options.averageRepeats
    ? averageRepeats(patches, options.repeatNoiseThreshold)
    : patches.map(p => ({ ...p, flags: { ...p.flags, repeatsCount: 1, repeatStdDev: 0 } }));

  const repeatsAveraged = patches.length - working.length;

  // ── B: Detect outliers ─────────────────────────────────────────────────────
  const beforeOutlier = working;
  if (options.autoRemoveOutliers === false) {
    // Flag only — do NOT remove
    working = detectOutliers(working, options.outlierMethod,
      options.outlierThresholdDE, options.outlierThresholdIQR);
  } else {
    working = detectOutliers(working, options.outlierMethod,
      options.outlierThresholdDE, options.outlierThresholdIQR);
    // Remove confirmed outliers (auto mode)
    working = working.filter(p => !p.flags.outlier);
  }
  const outliersDetected = working.filter(p => p.flags.outlier).length;
  const outliersRemoved  = beforeOutlier.length - working.filter(p => !p.flags.outlier).length - outliersDetected;

  // ── C: Smooth ─────────────────────────────────────────────────────────────
  if (options.smoothingMethod === 'gaussian' && options.smoothingSigma > 0) {
    const grouped = groupBy(working);
    const smoothed: MeasuredPatch[] = [];
    for (const [, grp] of grouped) {
      smoothed.push(...smoothGroup(grp, options.smoothingSigma));
    }
    working = smoothed;
  }

  // ── D: Fill gaps ──────────────────────────────────────────────────────────
  const beforeFill = working.length;
  working = fillGaps(working, {
    interpolateGaps:      options.interpolateGaps,
    extrapolateEdges:     options.extrapolateEdges,
    maxExtrapolationSteps: options.maxExtrapolationSteps,
  });
  const gapsInterpolated = working.filter(p => p.flags.interpolated).length;
  const gapsExtrapolated = working.filter(p => p.flags.extrapolated).length;
  void beforeFill;

  // ── E: Validate coverage ──────────────────────────────────────────────────
  const noisyPatches = working.filter(p => p.flags.noisy).length;

  // ── Build per-group report ────────────────────────────────────────────────
  const grouped = groupBy(working);
  const perGroup: Partial<Record<PatchGroupId, GroupCleaningReport>> = {};
  for (const group of ALL_GROUPS) {
    const grp = grouped.get(group);
    if (!grp) continue;
    perGroup[group] = {
      group,
      inputCount:   patches.filter(p => p.group === group).length,
      outputCount:  grp.length,
      outliers:     grp.filter(p => p.flags.outlier).map(p => p.id),
      interpolated: grp.filter(p => p.flags.interpolated).map(p => p.id),
      extrapolated: grp.filter(p => p.flags.extrapolated).map(p => p.id),
      repeatStdDev: Object.fromEntries(
        grp.filter(p => p.flags.repeatStdDev > 0).map(p => [p.id, p.flags.repeatStdDev]),
      ),
    };
  }

  const report: CleaningReport = {
    repeatsAveraged,
    outliersDetected,
    outliersRemoved,
    gapsInterpolated,
    gapsExtrapolated,
    noisyPatches,
    perGroup,
  };

  return { patches: working, report };
}

// Export internal sub-functions for unit testing
export { residualOutliers, iqrOutliers };
